import type { Recht, Rolle } from "../daten/typen";
import { rechteAus } from "./rechte";
import type { Zeichenname } from "../bausteine/Zeichen";

/* ══════════════════════════════════════════════════════════════════
   Welche Wege am Telefon unten in der Leiste stehen — je Rolle.

   Luca, 17.09.2026: *„Vor allem muss ich als Verkäufer ein einfaches
   Menü haben, eine Bottom-Navbar … mit meinen Hauptpunkten: Tagesmappe,
   Start."*

   ── Warum das die Gliederung ist und nicht der Stapel ──────────────

   Bis hierher war `/handy` **ein** Schirm, der alles untereinander
   legte, was die Rolle durfte: Datum, „Der Tag auf dem Hof",
   Vorbereiten, Raus, Rein, „Verkauf", Ortsaufnahmen, Nachfassen,
   zuletzt besucht, vier Griffe. Wer beides darf — Disposition und
   Verwaltung —, bekam beide Mappen hintereinander und rollte an einer
   Liste vorbei, die ihm gerade nichts sagt. Luca: *„das sieht wirklich
   nicht gut aus."*

   Die Leiste ist die Antwort darauf: **ein Schirm je Punkt.** Was
   vorher übereinanderstand, steht jetzt nebeneinander, und der Daumen
   wechselt, statt zu rollen.

   ── Die Regeln ────────────────────────────────────────────────────

   * **Höchstens fünf Fächer.** Auf 390 px sind das 78 px je Fach; bei
     sechs passt das Wort nicht mehr darunter.
   * **Ein Wort, ein Zeichen.** Kein Wort, das umbricht.
   * **Was nicht erlaubt ist, erscheint nicht** — kein Ausgrauen
     (28-designkonzept.md). Die Tabelle hier ist Wegweisung; der Riegel
     ist die API.
   * **Der Rest steht unter „Mehr"**, nicht in einem sechsten Fach.

   ── Je Rolle ──────────────────────────────────────────────────────

   | Rolle | Leiste |
   |---|---|
   | `technik` | Heute · Mehr |
   | `aussendienst` | Start · Suche · Preis · Angebot · Mehr |
   | `disposition`, `verwaltung` | Heute · Start · Suche · Preis · Mehr |
   | `buchhaltung` (und jede Rolle ohne eigene Fächer) | Mehr |
   | keine Rolle | keine Leiste — dort steht nur der freundliche Satz |

   **Jede Rolle hat „Mehr" als letztes Fach** (17.09.2026,
   40-mobiles-grundgeruest.md, Schritt 1). Menüknopf und Schublade sind am
   Telefon weggefallen; Abmelden, Darstellung, große Ansicht und die Punkte
   der Seitenleiste stehen nur noch dort (`Mehrblatt.tsx`). Beim Vertrieb
   ist dafür „Aufgaben" unter „Mehr" gerückt — die kleinste Änderung an
   seinen Fächern. Welche Fächer es endgültig sind, bespricht Luca.

   **Seit dem 17.09.2026 steht die Leiste am Telefon über jeder Maske**
   (`Handyleiste.tsx`), nicht nur unter `/handy…` — und seit dem 19.09.2026
   gibt es `/handy…` gar nicht mehr (`schale/altadressen.ts`): Die Wege
   heißen nach der Aufgabe, `/heute`, `/einsatz/…`, `/nachfassen`. Eine Rolle ohne eigene
   Fächer bekommt deshalb trotzdem eine — mit „Mehr", und darin die Punkte
   der Seitenleiste, die sie darf (`schale/Mehrblatt.tsx`). Sonst stünde
   die Buchhaltung am Telefon auf der Abrechnung und käme nur über den
   Menüknopf wieder heraus. Welche Fächer darüber hinaus in die Leiste
   gehören, ist offen: Das klärt das Gespräch über die Menüs.

   **„Mehr" ist ein Blatt und kein Weg.** Es öffnet sich über der Maske,
   die stehen bleibt; `/mehr` öffnet es nur noch und leitet zurück. Der Eintrag unten
   behält den Weg als Kennung — `aktivesFach` fragt ihn, damit „Mehr"
   leuchtet, wenn man über das Blatt auf einem seiner Wege steht.

   **„Start" ist `/start`, für jede Rolle** (seit 17.09.2026). Bis dahin
   hieß das Fach beim reinen Verkauf `/handy` und bei allen anderen
   `/handy/start` — zwei Adressen für denselben Schirm. Jetzt zeichnet
   die Startseite am Telefon den Schirm der Rolle, und „Heute" ist nur
   noch die Tagesmappe (seit dem 19.09.2026 unter `/heute`).

   ── „Zeiten" ist weg — 18.09.2026 ─────────────────────────────────

   Luca, gefragt, ob der Reiter „Zeiten" weg kann: *„kann weg? wir erfassen
   die zeiten ja über die vorgänge."*

   Der Schirm `/handy/zeiten` war eine **zweite Liste für dieselbe Sache**:
   dieselben Einsätze des Tages wie „Heute", dieselbe Uhr, dieselbe Zeile in
   denselben Einsatz. Erfasst wurde dort seit dem 17.09. ohnehin nichts mehr
   — gehandelt wird im Einsatz (`masken/handy/Einsatz.tsx`), und die Zeit
   gehört dem Vorgang (Regel 9). Die Adresse leitet dauerhaft auf „Heute"
   um (`router.tsx`).

   **Die Technik behält damit zwei Fächer: Heute · Mehr.** Das ist wenig und
   trotzdem keine Sackgasse: „Mehr" trägt für jede Rolle die Darstellung,
   die große Ansicht und das Abmelden (`Mehrblatt.tsx`) — man kommt also von
   jedem Schirm wieder heraus. Ein drittes Fach hätte nur erfunden werden
   können: Preis und Nachschlagen sieht der technische Außendienst nicht
   (`masken/handy/rechte.ts`), und alles andere liegt unter „Heute". Ein
   Fach, das auf denselben Schirm zeigt, war genau der Fehler, der hier
   gerade behoben wurde.
   ══════════════════════════════════════════════════════════════════ */

export type Handyweg = {
  weg: string;
  /** Der i18n-Schlüssel des Worts unter dem Zeichen. */
  text: string;
  zeichen: Zeichenname;
  /** Der Satz darunter — nur in der Liste „Mehr", nicht in der Leiste. */
  satz?: string;
  /** Weitere Wege, die zu diesem Fach gehören, ohne unter ihm zu liegen. */
  unter?: string[];
  /**
   * Öffnet dieses Fach ein **Blatt** statt die Maske zu wechseln?
   *
   * Ein Blatt ist kein Ort: Die Maske darunter behält Adresse und Zustand,
   * und es schließt mit der Zurückgeste. Bis zum 17.09.2026 konnte ein Fach
   * nur einen Weg tragen — „Preis" und „Nachschlagen" liefen deshalb über
   * ihre Adresse, die das Blatt aufmacht und sofort zurückleitet: ein
   * Schritt durch den Router, um etwas zu öffnen, das über der Maske liegt.
   * Jetzt tippt man das Fach und das Blatt ist auf.
   *
   * **Der Weg bleibt trotzdem stehen.** Er ist die Kennung des Fachs
   * (`aktivesFach`) und die Adresse, die Lesezeichen und installierte
   * Telefone kennen; sie leitet weiter um (`router.tsx`).
   */
  blatt?: "mehr" | "nachschlagen";
};

/** Die Tagesmappe. Bis zum 19.09.2026 `/handy` (`schale/altadressen.ts`).
 *  Der Einsatz liegt seitdem nicht mehr darunter, sondern daneben
 *  (`/einsatz/…`) — er gehört aber zu „Heute": Wer einen Einsatz aus der
 *  Tagesmappe öffnet, soll das Fach leuchten sehen, aus dem er kam. */
const HEUTE: Handyweg = {
  weg: "/heute", text: "handyLeiste.heute", zeichen: "heute",
  /* `/start` steht mit darunter: Die Altadresse leitet auf `/heute`, und
     während der Umleitung soll das Fach nicht dunkel werden (21.09.2026). */
  unter: ["/einsatz", "/start"],
};
/* **Das Fach „Start" ist am 21.09.2026 weggefallen.** Es führte auf `/start`
   und damit auf einen Schirm, der einen Abschnitt „Heute" trug — vier Zeilen
   aus derselben Liste, die das Fach daneben ganz zeigte. Luca: *„Wie kann man
   heute und Start zusammenbringen? Weil es klingt so dumm: heute und Start."*

   Beides steht jetzt in `/heute` (`masken/HeuteTelefon.tsx`), und dadurch
   wird in der Leiste ein Platz frei: Bei Disposition und Verwaltung rückt
   „Preis" aus „Mehr" wieder herauf. */
/* ── „Suche" statt „Kunden" — 19.09.2026 ───────────────────────────

   Luca, nach dem Vergleich mit OVIS: *„Die Suche findet alles, und du
   machst mit Chips einmal den Filter darauf. Dann machst du nämlich unten
   nicht mehr ‚Kunden', sondern ‚Suche', und dann suchst du da einfach
   alles."*

   **Das Fach „Kunden" geht in der Suche auf** (`masken/SucheTelefon.tsx`):
   Kunden sind dort ein Chip, und `/kunden` zeichnet am Telefon dieselbe
   Fläche mit diesem Chip gesetzt. Deshalb gehört `/kunden` unter dieses
   Fach — wer in einer Kundenakte steht, ist in der Suche, und dort leuchtet
   die Marke.

   **Damit kehrt sich eine Entscheidung vom 18.09.2026 um**, und zwar mit
   Grund: Damals hieß es, ein Fach „Suche" wäre die zweite Tür zur Lupe im
   Kopf. Die Lupe im Kopf ist am Telefon deshalb jetzt weg (`Schale.tsx`) —
   eine Tür, und sie steht dort, wo der Daumen ist. Oben bleibt das
   Nachschlagen mit dem Euro im Zeichen: Es beantwortet die Preisfrage und
   ist ein Blatt über der Maske, kein Ort. */
const SUCHE: Handyweg = {
  weg: "/suche", text: "handyLeiste.suche", zeichen: "suche",
  unter: ["/kunden"],
};
/* Preis und Nachschlagen sind **dasselbe Blatt**: Lage und Preis stehen
   seit dem 17.09.2026 in einem (`masken/NachschlagenTelefon.tsx`). Das Fach
   „Preis" öffnet es ohne Vorbelegung — die kommt nur über die Adresse mit
   `?typ=` aus einer Maske heraus. */
/* **Seit Signal (Paket 5, 07.10.2026) eine Seite und kein Blatt mehr** — nach
   app-design-muster (`navigation.md` § 2): Ein Fach der Leiste ist ein Ort und
   keine Handlung (HIG: „Use a tab bar to support navigation, not to provide
   actions"). Entschieden von Luca am 07.10.2026 (F5). Das Nachschlagen über
   der Maske bleibt — als Lupe mit Euro im Kopf. */
const PREIS: Handyweg = {
  weg: "/preis", text: "handyLeiste.preis", zeichen: "preis",
};
const ANGEBOT: Handyweg = {
  weg: "/angebote/neu", text: "handyLeiste.angebot", zeichen: "angebot",
  satz: "handyLeiste.angebotSatz",
  unter: ["/angebote"],
};
const AUFGABEN: Handyweg = {
  weg: "/aufnahmen", text: "handyLeiste.aufgaben", zeichen: "aufgaben",
  satz: "handyLeiste.aufgabenSatz",
  // Seit dem 17.09.2026 die Ortsaufnahmen selbst: Sie zeichnen am Telefon
  // meine, den Pool und den Auszug „Nachfassen" (`AufnahmenTelefon.tsx`).
  // Die Begehung liegt seit dem 19.09.2026 darunter (`/aufnahmen/$id`); die
  // ganze Nachfassliste nicht, sie gehört aber dazu: Wer dort steht, ist bei
  // den Aufgaben.
  unter: ["/nachfassen"],
};
const NACHSCHLAGEN: Handyweg = {
  weg: "/nachschlagen", text: "handyLeiste.nachschlagen", zeichen: "nachschlagen",
  satz: "handyLeiste.nachschlagenSatz", blatt: "nachschlagen",
};
/* „Mehr" ist seit Signal (Paket 5) eine Wurzelseite mit Liste, kein Blatt —
   Muster `navigation.md` § 2: *„Mehr als Wurzelseite, nicht als Überlauf"*. */
const MEHR: Handyweg = { weg: "/mehr", text: "handyLeiste.mehr", zeichen: "mehr" };

/* ── Der Anlegeknopf in der Mitte — 18.09.2026 ─────────────────────

   Luca, nach dem Durchsehen der Handyfassung von OVIS: *„Kopier ruhig das
   Layout grundsätzlich ab, mit dem Plus-Button mittig und so weiter."*

   **Das Vorbild und sein Grund** (42-vorbild-ovis.md). Bei OVIS stehen von
   fünf Plätzen zwei für Handlungen und nicht für Orte; der Quelltext dort
   sagt warum: *„‚Anlegen‘ und ‚Suche‘ sind Handlungen und stehen trotzdem in
   der Leiste, weil sie die beiden Wege sind, die man aus JEDER Tiefe
   braucht: etwas festhalten und etwas finden."*

   **Bei uns ist nur das Anlegen ein Platz.** Das Finden steht bei uns fest
   oben in der Kopfleiste, und zwar doppelt — die Suche und das Nachschlagen
   mit dem Euro im Zeichen. Ein Fach dafür wäre die zweite Tür in denselben
   Raum, und das sagt `Schale.tsx` seit dem 17.09.2026.

   **Je Rolle genau einer, und er führt direkt.** OVIS öffnet ein Menü mit
   fünf Einträgen; bei uns ist die Hauptanlage je Rolle eine einzige Sache.
   **Regel 10:** *„Der Regelfall hat kein Angebot. Die Direktvermietung ist
   der Einstieg, das Angebot der Sonderfall."* Wer vermieten darf, legt eine
   Vermietung an; wer das nicht darf, aber Angebote schreibt, schreibt ein
   Angebot. Ein Menü dazwischen wäre ein Griff, der nichts entscheidet.

   **Wer nichts anlegen darf, bekommt keine Lücke.** Die Mitte fällt dann auf
   null zusammen (`handy-leiste.css`) — dieselbe Regel wie im Vorbild: *„eine
   Leiste mit einem Loch liest sich als kaputt, nicht als leer."* Das trifft
   heute den technischen Außendienst: Er fährt, er verkauft nicht. */

const VERMIETEN: Handyweg = {
  weg: "/vermieten", text: "handyLeiste.vermieten", zeichen: "start",
  satz: "handyLeiste.vermietenSatz",
};

/** Was dieser Rolle das Anlegen ist — in der Reihenfolge von Regel 10. */
const ANLEGERECHT: [Handyweg, Recht][] = [
  [VERMIETEN, "vermieten"],
  [ANGEBOT, "angebote"],
];

/**
 * Der Knopf in der Mitte der unteren Leiste — oder `null`.
 *
 * Er zählt **nicht** zu den Fächern: Ein Fach ist ein Ort, dieser Knopf ist
 * eine Handlung. Er steht deshalb auch nicht unter „Mehr" ein zweites Mal
 * (`mehrFuer` nimmt ihn heraus).
 */
export function anlegenFuer(rollen: Rolle[]): Handyweg | null {
  const hat = rechteAus(rollen);
  return ANLEGERECHT.find(([, recht]) => hat.includes(recht))?.[0] ?? null;
}

/** Welches Blatt dieses Fach öffnet — oder `null`, wenn es ein Weg ist. */
export function blattFach(p: Handyweg): "mehr" | "nachschlagen" | null {
  return p.blatt ?? null;
}

/* ── Ein Fach hängt an einem Recht — 18.09.2026 ────────────────────

   Bis hierher fragte die Leiste die Rolle: `tag` hieß „Disposition,
   Verwaltung oder Technik", `verkauf` hieß „Disposition, Verwaltung oder
   Außendienst". Luca: *„da hat ein Außendienst-Kranfahrer Start nicht als
   Recht."* Eine Rolle, die vier Fächer auf einmal mitbringt, kann das
   nicht abbilden.

   Jetzt trägt **jedes Fach sein Recht**, und die Leiste nimmt der Reihe
   nach, was erlaubt ist — höchstens vier, „Mehr" als fünftes. Die
   Reihenfolge ist die von früher, und deshalb kommt für Technik,
   Außendienst, Disposition und Verwaltung genau dieselbe Leiste heraus
   wie am 17.09.2026.

   **Die Buchhaltung bekommt Fächer statt nur „Mehr".** Sie hatte
   `start`, `kunden`, `preise` und `angebote` immer schon — nur führte
   kein Fach dorthin, weil sie weder „lenkt" noch „verkauft". Das war die
   Lücke der Rollenlogik und nicht ihre Absicht. */

/** Welches Recht ein Fach verlangt. */
const FACHRECHT: [Handyweg, Recht][] = [
  /* **Ein Fach für beide Hälften** (21.09.2026): Wer den Tag darf oder das
     Anfangen darf, bekommt „Heute" — was darin steht, entscheidet die Maske
     je Recht. Ein Fach zweimal in dieser Liste hieße ein Fach zweimal in der
     Leiste, deshalb steht es einmal da und `leisteFuer` fragt beide Rechte. */
  [HEUTE, "tagesmappe"],
  [SUCHE, "kunden"],
  [PREIS, "preise"],
  [ANGEBOT, "angebote"],
];

/** Das zweite Recht, das ein Fach auch öffnet — bisher nur „Heute" (0156er Tag). */
const ZWEITRECHT: Partial<Record<string, Recht>> = { "/heute": "start" };

/** Alles, was unter „Mehr" stehen kann, mit seinem Recht. */
const MEHRRECHT: [Handyweg, Recht][] = [
  [NACHSCHLAGEN, "preise"],
  [ANGEBOT, "angebote"],
  [AUFGABEN, "aufnahmen"],
];

/**
 * Die Fächer der unteren Leiste. Leer heißt: keine Leiste.
 *
 * Wer gar nichts darf, bekommt sie nicht — auf seinem Schirm steht ein
 * Satz und sonst nichts, und eine Leiste darunter verspräche Wege, die es
 * für ihn noch nicht gibt.
 *
 * **Höchstens fünf Plätze, „Mehr" als letztes** (die Regel oben).
 *
 * ── Einer der fünf ist seit dem 18.09.2026 der Anlegeknopf ──────────
 *
 * Bis dahin waren es vier Fächer und „Mehr". Der Knopf in der Mitte nimmt
 * einen Platz, also sind es **drei Fächer und „Mehr"** — gerechnet auf
 * 390 px: (390 − 64) ÷ 4 = **81 px je Fach**, also eher mehr als die 78 px
 * von vorher. Das Vorbild rechnet genauso (402 px, vier Fächer, 64 px
 * Mitte).
 *
 * **Was dabei herausfällt, ist beim Außendienst nichts und bei der
 * Disposition „Preis".** Beim Außendienst wird „Angebot" zum Anlegeknopf —
 * es war schon vorher `/angebote/neu`, also derselbe Weg an einem besseren
 * Platz. Bei Disposition und Verwaltung rückt „Preis" unter „Mehr", und das
 * ist die Auflösung einer offenen Frage und kein Verlust:
 * 28-designkonzept.md hält seit dem 18.09.2026 fest, dass das Nachschlagen
 * **zweimal** erreichbar war — als Fach unten und als Lupe im Kopf. Seit die
 * Kopfleiste am Telefon fest steht, steht die Lupe auf **jeder** Maske; das
 * Fach daneben war die zweite Tür.
 */
export function leisteFuer(rollen: Rolle[]): Handyweg[] {
  const hat = rechteAus(rollen);
  const anlegen = anlegenFuer(rollen);
  const faecher = FACHRECHT
    .filter(([fach, recht]) => {
      const zweit = ZWEITRECHT[fach.weg];
      return (hat.includes(recht) || (zweit ? hat.includes(zweit) : false))
        && fach.weg !== anlegen?.weg;
    })
    .map(([fach]) => fach);
  if (faecher.length === 0) return hat.length ? [MEHR] : [];
  return [...faecher.slice(0, 3), MEHR];
}

/**
 * Was unter „Mehr" steht: alles, was erlaubt ist und was nicht schon in
 * der Leiste steht. Abmelden und die große Ansicht kommen dort dazu —
 * sie sind kein Weg in eine Maske und stehen deshalb nicht hier.
 */
export function mehrFuer(rollen: Rolle[]): Handyweg[] {
  const hat = rechteAus(rollen);
  /* Der Anlegeknopf steht in der Mitte der Leiste und zählt mit: Sonst stünde
     „Angebot" beim Außendienst zweimal — einmal als Kreis in der Mitte und
     einmal als Zeile unter „Mehr". */
  const inLeiste = new Set([
    ...leisteFuer(rollen).map((p) => p.weg),
    ...(anlegenFuer(rollen) ? [anlegenFuer(rollen)!.weg] : []),
  ]);
  return MEHRRECHT
    .filter(([fach, recht]) => hat.includes(recht) && !inLeiste.has(fach.weg))
    .map(([fach]) => fach);
}

/** Jeder Weg, den die untere Leiste überhaupt kennt — auch die, die
 *  gerade unter „Mehr" liegen. Ohne diese Liste fiele ein Pfad, der zu
 *  keinem Fach der aktuellen Rolle gehört, auf den nächstkürzeren zurück
 *  (bis zum 19.09.2026 war das `/handy`, der Anfang von allen). */
const ALLE_WEGE: Handyweg[] = [
  HEUTE, SUCHE, PREIS, ANGEBOT, AUFGABEN, NACHSCHLAGEN, VERMIETEN, MEHR,
];

/**
 * Welches Fach zu diesem Weg gehört — oder `null`.
 *
 * Verglichen wird auf den **Anfang**, und der längste Treffer gewinnt:
 * `/aufnahmen/…` gehört zu AUFGABEN, auch wenn ein anderes Fach einen
 * kürzeren Anfang hätte. Dieselbe Regel wie in `rechte.ts`; zwei
 * verschiedene Regeln für dieselbe Frage wären zwei Antworten.
 *
 * **Gesucht wird in allen Wegen, nicht nur in den Fächern dieser Rolle.**
 * Sonst leuchtete ein Fach, in dem man nicht war: Am 17.09.2026 stand beim
 * Außendienst auf `/handy/nachschlagen` die Marke unter „Start", weil sein
 * Start damals `/handy` hieß und der kürzere Anfang der einzige Treffer
 * war.
 *
 * Was es gibt, aber nicht in der Leiste steht, liegt unter „Mehr" —
 * also leuchtet „Mehr", wenn es das Fach gibt. Hat die Rolle auch das
 * nicht, leuchtet nichts; das ist ehrlicher als ein falsches Fach.
 *
 * **Das gilt für jeden Weg, der zu keinem Fach gehört** (17.09.2026):
 * Die Leiste steht jetzt über allen Masken, und wer am Telefon auf
 * `/abrechnung` steht, ist über „Mehr" dorthin gekommen — dort stehen die
 * Punkte der Seitenleiste.
 */
export function aktivesFach(punkte: Handyweg[], pfad: string): string | null {
  const passt = (weg: string) => pfad === weg || pfad.startsWith(`${weg}/`);
  const mehr = punkte.some((p) => p.weg === MEHR.weg) ? MEHR.weg : null;
  let treffer: string | null = null;
  let laenge = -1;
  for (const p of [...punkte, ...ALLE_WEGE]) {
    for (const weg of [p.weg, ...(p.unter ?? [])]) {
      if (passt(weg) && weg.length > laenge) {
        treffer = p.weg;
        laenge = weg.length;
      }
    }
  }
  if (treffer === null) return mehr;
  if (punkte.some((p) => p.weg === treffer)) return treffer;
  return mehr;
}
